import { z } from 'zod';

import { LANGUAGE, languageEnumBase, type LanguageEnumBaseData } from '../constants/language';
import type { Trim } from '../types/common';
import { silenceError } from '../utils/common';
import { isDefined, isEmpty } from '../utils/guards';
import { get } from '../utils/object';

/**
 * Schema for translation text objects containing translations in different languages
 * @example
 * ```ts
 * const text: TranslationText = {
 *   cs: "Ahoj světe",
 *   en: "Hello world",
 *   de: "Hallo Welt",
 *   sk: "Ahoj svet"
 * };
 * ```
 */
export const translationTextSchema = z
	.object({
		cs: z.string(),
		en: z.string(),
		de: z.string(),
		sk: z.string(),
	})
	.partial();
export type TranslationText = z.infer<typeof translationTextSchema>;

/**
 * Convert count to plural form based on the language
 * @see https://localizely.com/language-plural-rules/
 * @param count The count to convert
 * @param translationLanguage The languageId to convert the count to
 * @returns The plural form of the count (one, few, many, other)
 * @example
 * ```ts
 * countToPlural(1, LANGUAGE.CZECH) // returns 'one'
 * countToPlural(2, LANGUAGE.CZECH) // returns 'few'
 * countToPlural(5, LANGUAGE.CZECH) // returns 'other'
 * countToPlural(1.5, LANGUAGE.CZECH) // returns 'many'
 * ```
 */
const countToPlural = (count: number, translationLanguage: LANGUAGE) => {
	switch (translationLanguage) {
		case LANGUAGE.CZECH:
		case LANGUAGE.SLOVAK: {
			if (count === 1) return 'one';
			if (count === 2) return 'few';
			/** floating point count */
			if (count % 1 !== 0) return 'many';
			return 'other';
		}
		default: {
			if (count === 1) return 'one';
			return 'other';
		}
	}
};

/**
 * Interpolates the translation with the values
 * @param translation The translation string
 * @param values The values to interpolate
 * @returns The interpolated translation
 * @example
 * ```ts
 * interpolate("Hello {{name}}", { name: "John" }) // returns "Hello John"
 * interpolate("You have {{count}} messages", { count: 5 }) // returns "You have 5 messages"
 * ```
 */
const interpolate = (translation: string, values: Record<string, string | number> | null) => {
	/** no interpolation needed if no values */
	if (!isDefined(values)) {
		return translation;
	}

	const regex = /{{\s*([^{}\s]*)\s*}}/g;
	return translation.replace(regex, (match, key) => {
		if (values[key] === undefined) {
			console.error(`Missing interpolation value for key "${key}"`);
			return match;
		}
		return String(values[key]);
	});
};

/**
 * Translation resource type that supports nested objects with string values (deep nesting supported)
 * @template TBaseResource - The base resource type
 * @returns The typed translation resources record
 * @example
 * ```ts
 * type Resources = TranslationResource<{
 *   greeting: string;
 *   farewell: string;
 *   nested: {
 *     greeting: string;
 *     farewell: string;
 *     deeply: {
 *       nested: {
 *         greeting: string;
 *       };
 *     };
 *   };
 * }>;
 * ```
 */
export type TranslationResource = Record<string, string | Record<string, any>>;

/**
 * Translation resources type
 * @template TBaseResource - The base resource type
 * @returns The typed translation resources record
 * @example
 * ```ts
 * type Resources = TFnResources<{
 *   greeting: string;
 *   farewell: string;
 * }>;
 * ```
 */
export type TFnResources<TBaseResource extends TranslationResource> = Record<LANGUAGE, TBaseResource>;

/**
 * Represents a nested type value accessed via dot notation
 * Handles both nested objects and keys that contain dots
 * @template T - The object type
 * @template Path - The dot notation path
 * @template DirectKeyAccess - Whether to prioritize direct key access
 * @example
 * ```ts
 * type UserType = {
 *   name: string;
 *   profile: {
 *     age: number;
 *     settings: {
 *       theme: string;
 *     }
 *   },
 *   'name.with.dots': string
 * };
 *
 * type ThemeType = DotValue<UserType, 'profile.settings.theme'>; // string (nested access)
 * type DotKeyType = DotValue<UserType, 'name.with.dots', true>; // string (direct key access)
 * ```
 */
export type DotValue<T, Path extends string, DirectKeyAccess extends boolean = true> =
	/** First check if the full path is a direct key of the object and DirectKeyAccess is true */
	DirectKeyAccess extends true
		? Path extends keyof T
			? T[Path]
			: /** Otherwise traverse the path via dots */
			Path extends `${infer Key}.${infer Rest}`
			? Key extends keyof T
				? DotValue<T[Key], Rest, false>
				: never
			: Path extends keyof T
			? T[Path]
			: never
		: /** When DirectKeyAccess is false, always traverse the path */
		Path extends `${infer Key}.${infer Rest}`
		? Key extends keyof T
			? DotValue<T[Key], Rest, false>
			: never
		: Path extends keyof T
		? T[Path]
		: never;

/**
 * Get all path keys of an object with deep nesting
 * @template T - The source object type
 * @template Prefix - Current path prefix (used for recursion)
 */
export type PathKeys<T, Prefix extends string = ''> = {
	[K in keyof T]: K extends string
		? Prefix extends ''
			? T[K] extends Record<PropertyKey, any>
				? K | `${K}.${PathKeys<T[K], ''>}`
				: K
			: T[K] extends Record<PropertyKey, any>
			? `${Prefix}.${K}` | `${Prefix}.${K}.${PathKeys<T[K], ''>}`
			: `${Prefix}.${K}`
		: never;
}[keyof T & string];

/**
 * Helper type for extracting all possible dot-notation paths from language-indexed resources
 * @template TResources - The language resources object
 */
export type DotPaths<TResources extends Record<LANGUAGE, any>> = TResources extends any
	? TResources extends Record<LANGUAGE, any>
		? PathKeys<TResources[LANGUAGE.CZECH]>
		: PathKeys<TResources>
	: never;

/**
 * Helper type to extract just the key structure from a resource object
 * This allows different translation files to have different string values
 * while enforcing the same key structure
 * @template T - The source resource object type
 */
export type ResourceKeysStructure<T extends Record<string, any>> = {
	[K in keyof T]: T[K] extends string
		? string
		: {
				[NK in keyof T[K]]: T[K][NK] extends string ? string : ResourceKeysStructure<T[K][NK]>;
		  };
};

/**
 * Extract string interpolation tokens for a known translation string
 * Works with tokens in the format {{tokenName}}
 * @template T - The translation string to extract tokens from
 */
export type ExtractTokens<T extends string> = T extends `${string}{{${infer Token}}}${infer Rest}`
	? { [K in Trim<Token>]: string | number } & ExtractTokens<Rest>
	: {};

/**
 * Determines if a translation string has interpolation tokens
 * @template T - The translation string to check
 */
export type HasTokens<T extends string> = T extends `${string}{{${string}}}${string}` ? true : false;

/**
 * Type-safe interpolation tokens for a translation string
 * @template T - The translation string to get interpolation tokens for
 */
export type HasInterpolation<T extends string> = HasTokens<T> extends true ? ExtractTokens<T> : null;

/**
 * Helper type to extract translation string type from resources by path
 * @template TResources - The resources object
 * @template TPath - The dot notation path to the translation
 */
export type ExtractTranslationString<TResources extends Record<LANGUAGE, any>, TPath extends string> = TPath extends keyof TResources[LANGUAGE.CZECH]
	? TResources[LANGUAGE.CZECH][TPath] extends string
		? TResources[LANGUAGE.CZECH][TPath]
		: never
	: TPath extends `${infer Prefix}.${infer Suffix}`
	? DotValue<TResources[LANGUAGE.CZECH], TPath, true> extends string
		? DotValue<TResources[LANGUAGE.CZECH], TPath, true>
		: string
	: string;

/**
 * Enhanced translation function that preserves interpolation type safety
 * @template TResources - The resources to translate
 * @returns The translated string with type-safe interpolation
 */
export type TFn<TResources extends Record<LANGUAGE, any>> = <TPath extends DotPaths<TResources>>(
	key: TPath,
	interpolation: HasInterpolation<ExtractTranslationString<TResources, TPath>>,
	options?: TFnOptions,
) => string;

/**
 * Translation function options
 * @returns The translation options
 * @example
 * ```ts
 * const options: TFnOptions = {
 *   count: 5,
 *   context: 'menu',
 *   language: LANGUAGE.ENGLISH,
 *   defaultValue: 'Default text'
 * };
 * ```
 */
export interface TFnOptions {
	context?: string | number;
	count?: number;
	language?: LANGUAGE;
	defaultValue?: unknown;
}

/**
 * Creates a translation function with the given language and resources.
 * @param translationLanguage - The language to translate to
 * @param resources - The translation resources
 * @returns A translation function
 * @example
 * ```ts
 * const resources = {
 *   [LANGUAGE.ENGLISH]: {
 *     greeting: 'Hello {{name}}',
 *     farewell: 'Goodbye {{name}}',
 *     nested: {
 *       greeting: 'Hello from nested object {{name}}',
 *       deeper: {
 *         greeting: 'Hello from deeply nested object {{name}}'
 *       }
 *     }
 *   }
 * };
 * const t = createTFunction(LANGUAGE.ENGLISH, resources);
 * t('greeting', { name: 'John' }); // "Hello John"
 * t('nested.greeting', { name: 'John' }); // "Hello from nested object John"
 * t('nested.deeper.greeting', { name: 'John' }); // "Hello from deeply nested object John"
 * ```
 */
export const createTFunction =
	<TResources extends Record<LANGUAGE, any>>(translationLanguage: LANGUAGE, resources: TResources) =>
	<TPath extends DotPaths<TResources>>(
		key: TPath,
		interpolation: HasInterpolation<ExtractTranslationString<TResources, TPath>>,
		options: TFnOptions = {},
	) => {
		/** accept different language from options if requested, otherwise use the current */
		const lang = options.language ?? translationLanguage;
		const langRes = resources[lang] ?? resources;
		/** account for context suffix and count */
		const suffixWithContext = options.context ? `_${options.context}` : '';
		const suffixWithCount = isDefined(options.count) ? `_${countToPlural(options.count, lang)}` : '';
		/** find the possible translation key */
		const fullSuffix = `${suffixWithContext}${suffixWithCount}`;
		const keyStr = String(key);

		/** try to get translations with context and count */
		const possibleTranslationWithContextAndCount = get(langRes, `${keyStr}${fullSuffix}`);
		const possibleTranslationWithCount = get(langRes, `${keyStr}${suffixWithCount}`);
		const possibleTranslationWithContext = get(langRes, `${keyStr}${suffixWithContext}`);
		const possibleTranslationBase = get(langRes, keyStr);

		const possibleTranslation =
			possibleTranslationWithContextAndCount ?? possibleTranslationWithCount ?? possibleTranslationWithContext ?? possibleTranslationBase;

		/** prepare interpolation values with count if needed */
		let interpolationValues = interpolation;
		if (isDefined(options.count) && isDefined(interpolationValues)) {
			interpolationValues = {
				...interpolationValues,
				count: options.count,
			};
		}

		/** return fallback translation */
		if (isEmpty(possibleTranslation)) {
			console.error(`Translation for key "${keyStr}" not found!`);
			/** return default language (cs) fallback translation */
			const defaultFallback = options.defaultValue ?? get(resources[LANGUAGE.CZECH], keyStr);
			if (isEmpty(defaultFallback)) {
				console.error(`Fallback translation for key "${keyStr}" not found!`);
				return keyStr;
			}
			return interpolate(defaultFallback, interpolationValues);
		}

		/** return the translation */
		return interpolate(possibleTranslation, interpolationValues);
	};

/**
 * Gets all possible prefixes from a dot-separated string
 * @template T - The input string type
 * @example
 * ```ts
 * type Prefixes = GetPrefix<'a.b.c'>;
 * // 'a' | 'a.b' | 'a.b.c'
 * ```
 */
export type GetPrefix<T extends string> = T extends `${infer Prefix}.${infer Rest}` ? Prefix | `${Prefix}.${GetPrefix<Rest>}` : T;

/**
 * Gets all possible object key prefixes
 * @template T - The object type
 * @example
 * ```ts
 * type Prefixes = ObjectPrefixes<{
 *   'a.b.c': string,
 *   'a.b.d': string
 * }>;
 * // '' | 'a' | 'a.b'
 * ```
 */
export type ObjectPrefixes<T> = GetPrefix<keyof T & string> | '';

/**
 * Adds a prefix to all keys in an object type
 * @template TPrefix - The prefix to add
 * @template TObj - The object type
 * @example
 * ```ts
 * type Prefixed = PrefixKeys<'prefix', {
 *   key1: string,
 *   key2: string
 * }>;
 * // {
 * //   'prefix.key1': string,
 * //   'prefix.key2': string
 * // }
 * ```
 */
export type PrefixKeys<TPrefix extends ObjectPrefixes<TObj>, TObj extends TranslationResource> = {
	[K in keyof TObj as K extends string ? `${TPrefix}.${K}` : never]: TObj[K];
};

/**
 * Creates a translation function with prefixed keys
 * @template TResources - Translation resources by language
 * @template TPrefix - Optional prefix for translation keys
 * @example
 * ```ts
 * const t = createPrefixedTFunction(LANGUAGE.ENGLISH, {
 *   [LANGUAGE.ENGLISH]: {
 *     'errors.notFound': 'Page not found',
 *     errors: {
 *       validation: 'Validation error',
 *       fields: {
 *         name: 'Name is required'
 *       }
 *     }
 *   }
 * }, 'errors');
 * t('notFound', null); // "Page not found"
 * t('validation', null); // "Validation error"
 * t('fields.name', null); // "Name is required"
 *
 * // With empty prefix - access everything directly
 * const t2 = createPrefixedTFunction(LANGUAGE.ENGLISH, resources);
 * t2('greeting', { name: 'John' }); // "Hello John"
 * t2('errors.notFound', null); // "Page not found"
 * ```
 */
export function createPrefixedTFunction<
	TResources extends Record<LANGUAGE, Record<string, any>>,
	TPrefix extends ObjectPrefixes<TResources[LANGUAGE.CZECH]> = '',
>(language: LANGUAGE, resources: TResources, prefix: TPrefix = '' as TPrefix) {
	/** create a type-safe function for prefixed keys */
	return <TPath extends TPrefix extends '' ? DotPaths<TResources> : DotPaths<DotValue<TResources[LANGUAGE.CZECH], TPrefix>>>(
		key: TPath,
		tokens: HasInterpolation<ExtractTranslationString<TResources, TPrefix extends '' ? TPath : `${TPrefix}.${TPath}`>>,
		options: TFnOptions = {},
	) => {
		const fullKey = !isEmpty(prefix) ? `${prefix}.${String(key)}` : String(key);
		return createTFunction(language, resources)(fullKey as any, tokens, options);
	};
}

/**
 * Custom i18n translation helper
 * @param translationLanguage - The languageId to translate to
 * @param resources - The resources to be used for translation
 * @author filipditrich <ditrich@nfctron.com>
 * @example
 * ```ts
 * const { t, i18n, language } = getTranslation(LANGUAGE.ENGLISH, resources);
 * t('greeting', { name: 'John' }); // "Hello John"
 * t('nested.greeting', { name: 'John' }); // "Hello from nested object John"
 * console.log(i18n); // All translations for English
 * console.log(language); // English language metadata
 * ```
 */
export const getTranslation = <TResources extends Record<LANGUAGE, any>>(translationLanguage: LANGUAGE, resources: TResources) => {
	const language: LanguageEnumBaseData = languageEnumBase.get(translationLanguage);

	return {
		/** translations */
		i18n: resources?.[translationLanguage] ?? {},
		/** t function */
		t: createTFunction<TResources>(translationLanguage, resources),
		/** language */
		language,
		/** resources */
		resources,
	};
};

/**
 * Creates a translation map from a base key and an enum type.
 * This type is useful for creating typed translation maps for enum-based values
 * where you need a default label and labels for each enum value.
 *
 * @template TBaseKey - The base key type (e.g. 'label')
 * @template TEnum - The enum type to create translations for
 * @returns A record type with keys for the base and each enum value
 * @example
 * ```ts
 * // For an enum like LANGUAGE = { CZECH = 1, ENGLISH = 3, ... }
 * // Creates a type with keys: 'label', 'label_1', 'label_3', etc.
 * type LanguageLabels = TypedEnumMap<'label', LANGUAGE>;
 * ```
 */
export type TypedEnumMap<TBaseKey extends string, TEnum extends string | number> = Record<TBaseKey | `${TBaseKey}_${TEnum}`, string>;

/**
 * From TranslationText (a record of cs|en|de|sk|... texts) get the translation in the specified language.
 * @see @nfctron/nexus-core cart-extra-data.ts
 * @param translationText A record of intl texts
 * @param translationLanguage The language to get the translation for
 * @returns The translation in the specified language or null if not found.
 * @example
 * ```ts
 * const text: TranslationText = {
 *   cs: "Ahoj",
 *   en: "Hello"
 * };
 * getTranslationText(text, LANGUAGE.ENGLISH); // "Hello"
 * getTranslationText(text, 'en'); // "Hello"
 * getTranslationText(text, LANGUAGE.GERMAN); // null
 * ```
 */
export const getTranslationText = (translationText: TranslationText | undefined, translationLanguage: LANGUAGE | string) => {
	const language = silenceError(
		() => languageEnumBase.get(translationLanguage, typeof translationLanguage === 'string' ? 'code' : 'languageId'),
		languageEnumBase.get(LANGUAGE.CZECH),
	);
	return translationText?.[language.code] ?? null;
};
